/**
 * Mobile App Bridge
 * 
 * This file handles communication between the React web app and the React Native mobile app.
 * When the web app is loaded inside the mobile app's WebView, it can:
 * - Open native IAP subscription modal
 * - Receive subscription status updates
 * - Check if user is subscribed
 */

class MobileAppBridge {
  constructor() {
    this.isInApp = window.isInApp === true;
    this.subscriptionCallbacks = [];
    this.initialized = false;

    console.log('🔍 Mobile App Bridge: Initializing...');
    console.log('🔍 window.isInApp =', window.isInApp);
    console.log('🔍 this.isInApp =', this.isInApp);

    if (this.isInApp) {
      console.log('✅ Detected: Running in mobile app!');
      this.init();
    } else {
      console.log('🌐 Detected: Running in web browser');
    }
  }

  init() {
    if (this.initialized) {
      console.log('⚠️ Already initialized, skipping');
      return;
    }

    console.log('🚀 Mobile App Bridge: Initializing for mobile app...');

    if (window.isInApp) {
      // Listen for messages from the mobile app
      window.addEventListener('message', this.handleMessage.bind(this));
      document.addEventListener('message', this.handleMessage.bind(this));
    }
    console.log('👂 Event listeners added for mobile app messages');

    // Check subscription status on init
    setTimeout(() => {
      console.log('⏰ Auto-checking subscription status...');
      this.checkSubscription();
    }, 1000);

    this.initialized = true;
    console.log('✅ Mobile App Bridge: Initialization complete!');
  }

  handleMessage(event) {
    // Only handle messages if we're in the mobile app
    if (!this.isInApp) return;

    try {
      // Check if event.data is already an object or a string
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data;
      } else {
        return; // Ignore other types
      }

      // Only process IAP-related messages
      if (!data.type || (data.type !== 'SUBSCRIPTION_STATUS' && data.type !== 'CHECK_SUBSCRIPTION')) {
        return; // Ignore non-IAP messages
      }

      console.log('📱 Mobile App IAP Message Received:', data);
      console.log('   Type:', data.type);

      if (data.type === 'SUBSCRIPTION_STATUS') {
        console.log('   ✅ Subscription Status Update:');
        console.log('      - Is Host:', data.isHost);
        console.log('      - Subscription:', data.subscription);

        // Notify all listeners about subscription status
        this.subscriptionCallbacks.forEach((callback, index) => {
          console.log(`   📣 Notifying listener ${index + 1}...`);
          callback({
            isHost: data.isHost,
            subscription: data.subscription
          });
        });
      }
    } catch (e) {
      // Silently ignore - probably not an IAP message
    }
  }

  /**
   * Open native subscription modal (IAP)
   * Call this when user clicks "Become a Host" button
   */
  openSubscription() {
    console.log('');
    console.log('🎯 openSubscription() called');
    console.log('   isInApp:', this.isInApp);
    console.log('   window.openSubscription exists:', typeof window.openSubscription);

    if (!this.isInApp) {
      console.warn('⚠️ Not in mobile app - subscription modal not available');
      console.log('   Returning false - should use web payment');
      return false;
    }

    if (window.openSubscription) {
      console.log('✅ window.openSubscription function found!');
      console.log('📱 Calling window.openSubscription()...');
      try {
        window.openSubscription();
        console.log('✅ Native subscription modal opened successfully!');
        return true;
      } catch (error) {
        console.error('❌ Error calling window.openSubscription():', error);
        return false;
      }
    }

    console.error('❌ window.openSubscription function NOT available');
    console.error('   This means the mobile app bridge is not properly injected');
    return false;
  }

  /**
   * Check current subscription status
   * Mobile app will respond with SUBSCRIPTION_STATUS message
   */
  checkSubscription() {
    console.log('🔍 checkSubscription() called');

    if (!this.isInApp) {
      console.log('   ⚠️ Not in mobile app, skipping');
      return;
    }

    try {
      console.log('   📤 Sending CHECK_SUBSCRIPTION message to mobile app...');
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'CHECK_SUBSCRIPTION'
      }));
      console.log('   ✅ Message sent, waiting for response...');
    } catch (error) {
      console.error('   ❌ Failed to check subscription:', error);
    }
  }

  /**
   * Subscribe to subscription status updates
   * @param {Function} callback - Called when subscription status changes
   * @returns {Function} unsubscribe function
   */
  onSubscriptionChange(callback) {
    this.subscriptionCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      this.subscriptionCallbacks = this.subscriptionCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Check if running in mobile app
   * @returns {boolean}
   */
  isInMobileApp() {
    return this.isInApp;
  }
}

// Create singleton instance
const mobileAppBridge = new MobileAppBridge();

export default mobileAppBridge;

